import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { TranslationReplacementAction } from '@course-authoring/shared-types';
import { lessonBlockTranslationKeys } from '../../api/lessonBlockTranslationApi';
import { lessonMetadataKeys } from '../../api/lessonMetadataApi';
import {
  lessonKeys,
  withEditedPublishState,
  type LessonEditorData,
} from '../../api/courseApi';
import {
  selectContentState,
  useEditorStore,
  type ContentState,
} from '../../store/editorStore';
import {
  persistGranularLessonSnapshot,
  type PersistGranularLessonOptions,
} from './lessonGranularPersistence';
import {
  DEFAULT_AUTOSAVE_INTERVAL_MS,
  useAutosaveInterval,
  useLatestRef,
  useScopeAwareAutosaveLifecycle,
} from '../../hooks/useScopeAwareAutosaveLifecycle';

export {
  persistGranularLessonSnapshot,
  type GranularLessonApi,
  type PersistGranularLessonOptions,
} from './lessonGranularPersistence';

export interface UseLessonBlockAutosaveOptions {
  enabled: boolean;
  scopeKey: string;
  lessonId: string;
  accountId: number | null;
  getSnapshot: () => ContentState;
  getReplacementActions?: () => Record<string, TranslationReplacementAction>;
  onBlockContentSaved?: PersistGranularLessonOptions['onBlockContentSaved'];
  intervalMs?: number;
}

export function useLessonBlockAutosave({
  enabled,
  scopeKey,
  lessonId,
  accountId,
  getSnapshot,
  getReplacementActions,
  onBlockContentSaved,
  intervalMs = DEFAULT_AUTOSAVE_INTERVAL_MS,
}: UseLessonBlockAutosaveOptions) {
  const queryClient = useQueryClient();
  const {
    status,
    error,
    captureScope,
    isCurrentScope,
    beginSave,
    markSaved,
    markFailed,
  } = useScopeAwareAutosaveLifecycle(scopeKey);
  const lastSavedSnapshotRef = useRef('');
  const pendingSaveSnapshotRef = useRef('');
  const inFlightRef = useRef<Promise<void> | null>(null);
  const getSnapshotRef = useLatestRef(getSnapshot);
  const getReplacementActionsRef = useLatestRef(getReplacementActions);
  const onBlockContentSavedRef = useLatestRef(onBlockContentSaved);

  useEffect(() => {
    inFlightRef.current = null;
  }, [scopeKey]);

  const persistLatest = useCallback(async function persist(): Promise<void> {
    const existing = inFlightRef.current;
    if (existing) {
      await existing;
      return persist();
    }
    const current = getSnapshotRef.current();
    const serializedCurrent = JSON.stringify(current);
    if (!lastSavedSnapshotRef.current || serializedCurrent === lastSavedSnapshotRef.current) return;
    const baseline = JSON.parse(lastSavedSnapshotRef.current) as ContentState;
    const submittedScope = captureScope();
    pendingSaveSnapshotRef.current = serializedCurrent;
    beginSave();

    let request: Promise<void>;
    request = persistGranularLessonSnapshot({
      lessonId,
      accountId,
      baseline,
      current,
      replacementActions: getReplacementActionsRef.current?.(),
      duplicateSourceByBlockId: useEditorStore.getState().duplicateSourceByBlockId,
      onIdMapping: (mapping) => useEditorStore.getState().reconcileBlockIds(mapping),
      onBlockReconciled: (blockId, submitted, saved) =>
        useEditorStore.getState().reconcileSavedBlock(blockId, submitted, saved),
      onBlockContentSaved: (...args) => onBlockContentSavedRef.current?.(...args),
    }).then((savedContent) => {
      if (!isCurrentScope(submittedScope)) return;
      const serializedSaved = JSON.stringify(savedContent);
      lastSavedSnapshotRef.current = serializedSaved;
      pendingSaveSnapshotRef.current = serializedSaved;
      queryClient.setQueryData<LessonEditorData>(
        lessonKeys.detail(lessonId, 'en', accountId),
        (previous) => ({
          ...savedContent,
          publishState: withEditedPublishState(previous),
          deleteEligibility: previous?.deleteEligibility ?? null,
        }),
      );
      // The narration manifest is resolved from persisted content plus
      // SpeechAudio freshness, so refresh it after every granular save.
      void queryClient.invalidateQueries({
        queryKey: lessonKeys.detail(lessonId, 'en', accountId),
      });
      void queryClient.invalidateQueries({
        queryKey: lessonMetadataKeys.detail(lessonId, 'en', accountId),
      });
      void queryClient.invalidateQueries({
        queryKey: lessonBlockTranslationKeys.detail(lessonId, 'en', accountId),
      });
      if (accountId === null) {
        queryClient.removeQueries({
          queryKey: [...lessonKeys.details(), lessonId],
          type: 'inactive',
        });
      }
      markSaved(submittedScope);
    }).catch((reason: unknown) => {
      markFailed(submittedScope, reason, 'Failed to save lesson');
      throw reason;
    }).finally(() => {
      if (inFlightRef.current === request) inFlightRef.current = null;
    });
    inFlightRef.current = request;
    return request;
  }, [accountId, beginSave, captureScope, isCurrentScope, lessonId, markFailed, markSaved, queryClient]);

  const flush = useCallback(async () => {
    while (true) {
      await persistLatest();
      const current = JSON.stringify(getSnapshotRef.current());
      if (!lastSavedSnapshotRef.current || current === lastSavedSnapshotRef.current) return;
    }
  }, [persistLatest]);

  useAutosaveInterval(enabled, intervalMs, () => {
    void persistLatest().catch(() => undefined);
  });

  const alignBaseline = useCallback((content?: ContentState) => {
    const snapshot = content ?? selectContentState(useEditorStore.getState());
    const serialized = JSON.stringify(snapshot);
    lastSavedSnapshotRef.current = serialized;
    pendingSaveSnapshotRef.current = serialized;
  }, []);

  return {
    status,
    error,
    lastSavedSnapshotRef,
    pendingSaveSnapshotRef,
    flush,
    alignBaseline,
  };
}
